/**
 * 「操作金鑰」（`X-Api-Token`）的**救援**入口——2026-10-27 起不再是寫入的必要條件。
 *
 * 背景：Worker 的寫入類端點（建帳號、`/say`、`/pm`、`/join`…）本來除了 session 之外還要
 * `x-api-token`（後端 `SERVICE_TOKEN`）。那把金鑰只是多一道「換分頁就要重貼」的麻煩，
 * 而使用者拿不到它，所以 worker 端已移除該閘門（`requireServiceToken` 保留但不再被
 * 寫入路徑呼叫）。**有 session 就能寫。**
 *
 * 這個元件因此降級為**被動**：
 * - **平常完全不渲染**：沒有浮動小按鈕、不佔版面、不提示、不擋任何操作。
 *   使用者永遠不會因為「金鑰未設定」被停下來。
 * - **只有真的收到後端「缺服務金鑰」的錯誤時**（`onApiTokenRequired`：某個部署仍開著舊閘門、
 *   或 `/api/sg/*` 走回舊路徑）才把設定框打開一次，並說明「不是登入過期」。
 * - 錯誤分類本身沒動（`error-copy.ts` 仍分得出「真的過期」與「服務金鑰未設定」），
 *   只是後者現在應該是罕見事件，而不是每次寫入都撞到。
 *
 * 其他不變：金鑰永不顯示明文（`password` 輸入框，畫面只給 `••••1234`）、存
 * `sessionStorage`（關掉分頁即失效，共用電腦不留憑證）。
 */
import { useEffect, useState } from 'react';
import { maskApiToken, onApiTokenRequired, readApiToken, writeApiToken } from '@chat-rea/console-ui';

export function ApiTokenSettings() {
  const [open, setOpen] = useState(false);
  /** 已存的鑰匙只留在 state 裡算遮蔽字串，**不放進 DOM**。 */
  const [saved, setSaved] = useState<string>(() => readApiToken());
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  // 後端說「缺 X-Api-Token」→ 直接把設定框叫出來（只有這條通知會開，session 過期不會）。
  useEffect(
    () =>
      onApiTokenRequired(() => {
        setSaved(readApiToken());
        setNotice('這個請求被後端擋下來：缺的是操作金鑰（X-Api-Token），不是登入過期。');
        setOpen(true);
      }),
    [],
  );

  // 刻意**不**依 IS_MOCK 隱藏，也刻意**不**渲染常駐按鈕：這個元件平常什麼都不畫，
  // 只有 `open`（收到後端「缺服務金鑰」）時才有東西出現在畫面上。
  const masked = maskApiToken(saved);
  const isSet = masked !== '';
  const draftTrimmed = draft.trim();

  const close = () => {
    setOpen(false);
    setDraft('');
  };

  const save = () => {
    if (draftTrimmed === '') return;
    writeApiToken(draftTrimmed);
    setSaved(readApiToken());
    setDraft('');
    setNotice('已存進這個分頁（sessionStorage）。按「重試」重送剛剛失敗的動作即可。');
  };

  const clear = () => {
    writeApiToken('');
    setSaved('');
    setDraft('');
    setNotice('已清除。');
  };

  return (
    <>
      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="api-token-title"
          data-testid="api-token-dialog"
        >
          <div className="w-full max-w-sm rounded-md border border-border bg-surface p-3 shadow-lg">
            <div className="flex items-start justify-between gap-2">
              <h2 id="api-token-title" className="text-[13px] font-medium text-text-primary">
                操作金鑰（X-Api-Token）
              </h2>
              <button
                type="button"
                onClick={close}
                className="cui-icon-btn"
                aria-label="關閉"
                data-testid="api-token-close"
              >
                ×
              </button>
            </div>

            <p className="mt-1 text-[11px] leading-relaxed text-text-tertiary">
              這個後端實例還要求寫入類動作（建帳號、送訊息、進房）多帶一把金鑰。
              <strong className="text-text-secondary">這不是登入問題</strong>：貼一次就好，
              不用重新登入。只存在這個分頁，關掉分頁就消失；畫面不會顯示明文。
            </p>

            <p className="mt-2 font-mono text-[11px] text-text-secondary" data-testid="api-token-status">
              {isSet ? `已設定：${masked}` : '未設定'}
            </p>

            <label className="mt-2 block text-[11px] text-text-tertiary" htmlFor="api-token-input">
              貼上金鑰
            </label>
            <input
              id="api-token-input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                save();
              }}
              placeholder="X-Api-Token"
              className="mt-1 w-full rounded border border-border bg-transparent px-2 py-1.5 font-mono text-[12px] text-text-primary"
              data-testid="api-token-input"
            />

            {notice === null ? null : (
              <p className="mt-2 text-[11px] leading-relaxed text-text-secondary" role="status" data-testid="api-token-notice">
                {notice}
              </p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={save}
                disabled={draftTrimmed === ''}
                className="cui-btn cui-btn--sm cui-btn--primary"
                data-testid="api-token-save"
              >
                儲存
              </button>
              <button
                type="button"
                onClick={clear}
                disabled={!isSet}
                className="cui-btn cui-btn--sm"
                data-testid="api-token-clear"
              >
                清除
              </button>
              <button type="button" onClick={close} className="cui-btn cui-btn--sm" data-testid="api-token-dismiss">
                關閉
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
